import queryString from 'query-string';
import {
  BalanceData,
  ChartData,
  IPaymentApiCall,
  IRestApiService,
  Payment
} from '../types';
import { PaymentTracker } from '../services/analytics/apiTrackers';

class PaymentApiCall implements IPaymentApiCall {
  private restApiService: IRestApiService;

  constructor(restApiService: IRestApiService) {
    this.restApiService = restApiService;
  }

  getChart(from: any, to: any, sources: string[], selectedPurposes: string[]): Promise<ChartData> {
    return this.restApiService.post('payments/chart', {
      from: from.format('DD-MM-YYYY'),
      to: to.format('DD-MM-YYYY'),
      sources,
      purposes: selectedPurposes,
    });
  }

  getChartByPurpose(from: any, to: any, sources: string[], selectedPurposes: string[]): Promise<ChartData> {
    return this.restApiService.post('payments/chart-purpose', {
      from: from.format('DD-MM-YYYY'),
      to: to.format('DD-MM-YYYY'),
      sources,
      purposes: selectedPurposes,
    });
  }

  movePayment(newPayment: Payment): Promise<Payment> {
    const startTime = Date.now();

    return this.restApiService.post('payments/move', newPayment)
      .then((result) => {
        PaymentTracker.trackMovePayment({
          payment: newPayment,
          success: true,
          duration: Date.now() - startTime
        });
        return result;
      })
      .catch((error) => {
        PaymentTracker.trackMovePayment({
          payment: newPayment,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  addPayment(clientId: string, newPayment: Payment): Promise<Payment> {
    const startTime = Date.now();

    return this.restApiService.post('payments/client/' + clientId, newPayment)
      .then((result) => {
        PaymentTracker.trackAddPayment({
          clientId,
          payment: newPayment,
          success: true,
          duration: Date.now() - startTime
        });
        return result;
      })
      .catch((error) => {
        PaymentTracker.trackAddPayment({
          clientId,
          payment: newPayment,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  reCreatedPayment(ownerId: string, paymentId: string, data: any): Promise<Payment> {
    const startTime = Date.now();

    return this.restApiService.post(
      `payments/${ownerId}/re-created/${paymentId}`,
      data
    )
      .then((result) => {
        PaymentTracker.trackReCreatedPayment({
          ownerId,
          paymentId,
          paymentData: data,
          success: true,
          duration: Date.now() - startTime
        });
        return result;
      })
      .catch((error) => {
        PaymentTracker.trackReCreatedPayment({
          ownerId,
          paymentId,
          paymentData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  addClientPayment(newPayment: Payment): Promise<Payment> {
    const startTime = Date.now();

    return this.restApiService.post('payments/client-payment', newPayment)
      .then((result) => {
        PaymentTracker.trackAddClientPayment({
          payment: newPayment,
          success: true,
          duration: Date.now() - startTime
        });
        return result;
      })
      .catch((error) => {
        PaymentTracker.trackAddClientPayment({
          payment: newPayment,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  updatePaymentPurpose(ownerId: string, id: string, data: any): Promise<Payment> {
    const startTime = Date.now();

    return this.restApiService.put(`payments/${ownerId}/${id}`, data)
      .then((result) => {
        PaymentTracker.trackUpdatePaymentPurpose({
          ownerId,
          id,
          updateData: data,
          success: true,
          duration: Date.now() - startTime
        });
        return result;
      })
      .catch((error) => {
        PaymentTracker.trackUpdatePaymentPurpose({
          ownerId,
          id,
          updateData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  updatePaymentConfirmationNote(ownerId: string, id: string, paymentConfirmationNote: string): Promise<Payment> {
    const startTime = Date.now();

    return this.restApiService.put(
      `payments/${ownerId}/${id}/confirmation-note`,
      { paymentConfirmationNote: paymentConfirmationNote }
    )
      .then((result) => {
        PaymentTracker.trackUpdatePaymentConfirmationNote({
          ownerId,
          id,
          confirmationNote: paymentConfirmationNote,
          success: true,
          duration: Date.now() - startTime
        });
        return result;
      })
      .catch((error) => {
        PaymentTracker.trackUpdatePaymentConfirmationNote({
          ownerId,
          id,
          confirmationNote: paymentConfirmationNote,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  addPaymentToAttendance(
    ownerId: string,
    clientId: string,
    attendanceId: string,
    payment: Payment,
    withCreditPayment: boolean,
    billingTag: string
  ): Promise<Payment> {
    const startTime = Date.now();

    return this.restApiService.post(
      `payments/${ownerId}/${clientId}/${attendanceId}/add-payment`,
      {
        payment: payment,
        withCreditPayment: withCreditPayment,
        billingTag: billingTag,
      }
    )
      .then((result) => {
        PaymentTracker.trackAddPaymentToAttendance({
          ownerId,
          clientId,
          attendanceId,
          payment,
          withCreditPayment,
          billingTag,
          success: true,
          duration: Date.now() - startTime
        });
        return result;
      })
      .catch((error) => {
        PaymentTracker.trackAddPaymentToAttendance({
          ownerId,
          clientId,
          attendanceId,
          payment,
          withCreditPayment,
          billingTag,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  addMyExpense(newPayment: Payment): Promise<Payment> {
    const startTime = Date.now();

    return this.restApiService.post('payments/client', newPayment)
      .then((result) => {
        PaymentTracker.trackAddMyExpense({
          payment: newPayment,
          success: true,
          duration: Date.now() - startTime
        });
        return result;
      })
      .catch((error) => {
        PaymentTracker.trackAddMyExpense({
          payment: newPayment,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  cancel(ownerId: string, id: string): Promise<void> {
    const startTime = Date.now();

    return this.restApiService.delete(`payments/${ownerId}/${id}`)
      .then((result) => {
        PaymentTracker.trackCancelPayment({
          ownerId,
          id,
          success: true,
          duration: Date.now() - startTime
        });
        return result;
      })
      .catch((error) => {
        PaymentTracker.trackCancelPayment({
          ownerId,
          id,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  getPaymentById(ownerId: string, paymentId: string): Promise<Payment> {
    let url = `payments/${ownerId}/payment/${paymentId}`;
    return this.restApiService.get(url);
  }

  getOnlinePaymentById(ownerId: string, orderId: string): Promise<Payment> {
    let url = `online-payment/${ownerId}/${orderId}`;
    return this.restApiService.get(url);
  }

  getPayments(
    clientId: string,
    from: any,
    to: any,
    showApproximatePayments: boolean,
    purposes?: string[],
    showCanceled?: boolean
  ): Promise<{ payments: Payment[] }> {
    let url = `payments/client/${clientId}?from=${from.format(
      'DD-MM-YYYY'
    )}&to=${to.format(
      'DD-MM-YYYY'
    )}&showCanceled=${showCanceled || false}&showApproximatePayments=${showApproximatePayments}${
      purposes ? '&' + queryString.stringify({ purposes }) : ''
    }`;
    return this.restApiService.get(url);
  }

  getTeacherPayments(
    teacherId: string,
    from: any,
    to: any,
    showApproximatePayments?: boolean,
    purposes?: string[],
    showCanceled?: boolean
  ): Promise<{ payments: Payment[] }> {
    let url = `payments/teacher/${teacherId}?from=${from.format(
      'DD-MM-YYYY'
    )}&to=${to.format('DD-MM-YYYY')}`;
    return this.restApiService.get(url);
  }

  getPublicPayments(
    ownerId: string,
    clientId: string,
    from: any,
    to: any,
    purposes?: string[]
  ): Promise<{ payments: Payment[] }> {
    let url = `payments/${ownerId}/public-client/${clientId}?from=${from.format(
      'DD-MM-YYYY'
    )}&to=${to.format('DD-MM-YYYY')}${
      purposes ? '&' + queryString.stringify({ purposes }) : ''
    }`;
    return this.restApiService.get(url);
  }

  getAttendanceReport(ownerId: string, from: any, to: any): Promise<any> {
    let url = `payments/${ownerId}/attendance-report?from=${from.format(
      'DD-MM-YYYY'
    )}&to=${to.format('DD-MM-YYYY')}`;
    return this.restApiService.get(url);
  }

  getPurposes(ownerId: string): Promise<any> {
    let url = `payments/${ownerId}/get-purposes`;
    return this.restApiService.get(url);
  }

  getClientRatePlanPayments(ownerId: string, clientRatePlanId: string): Promise<{ payments: Payment[] }> {
    let url = `payments/${ownerId}/rate-plan/${clientRatePlanId}`;
    return this.restApiService.get(url);
  }

  getSubPurposes(ownerId: string, purpose: string): Promise<any> {
    let url = `payments/${ownerId}/get-purposes`;
    return this.restApiService.get(url);
  }

  getReport(
    from: any,
    to: any,
    transactionType?: string,
    paymentType?: string | string[],
    showCanceled?: boolean,
    selectedSubPurposes?: string[],
    selectedSources?: string[],
    studioClientId?: string,
    teacherId?: string,
    ownerId?: string
  ): Promise<any> {
    let url = `payments/report`;
    if (ownerId) {
      url = `payments/report/${ownerId}`;
    }
    return this.restApiService.post(url, {
      from: from,
      to: to,
      showCanceled: showCanceled,
      transactionType: transactionType,
      paymentType: paymentType,
      selectedSubPurposes: selectedSubPurposes,
      selectedSources: selectedSources,
      studioClientId: studioClientId,
      teacherId: teacherId,
    });
  }

  getTransactionsReport(
    from: any,
    to: any,
    transactionType?: string,
    paymentType?: string,
    showCanceled?: boolean,
    selectedSubPurposes?: string[],
    selectedSources?: string[],
    showFromRatePlans?: boolean
  ): Promise<any> {
    return this.restApiService.post(`payments/transactions-report`, {
      from: from,
      to: to,
      showCanceled: showCanceled,
      transactionType: transactionType,
      paymentType: paymentType,
      selectedSubPurposes: selectedSubPurposes,
      selectedSources: selectedSources,
      showFromRatePlans: showFromRatePlans,
    });
  }

  getTeacherReport(from: any, to: any, teacherIds: string[]): Promise<any> {
    return this.restApiService.post(`payments/teacher-report`, {
      from: from,
      to: to,
      teacherIds: teacherIds,
    });
  }

  calculateSalary(from: any, to: any, ownerId: string, teacherId: string, dryRun: boolean): Promise<any> {
    return this.restApiService.post(
      `payments/${ownerId}/${teacherId}/calculate-salary`,
      {
        from: from.format('DD-MM-YYYY'),
        to: to.format('DD-MM-YYYY'),
        dryRun: dryRun,
      }
    );
  }

  getCashboxReport(): Promise<any> {
    return this.restApiService.post(`payments/chashbox-report`);
  }

  getReportStatistic(
    from: any,
    to: any,
    debitOnly: boolean,
    showCanceled: boolean,
    paymentType?: string,
    selectedSubPurposes?: string[]
  ): Promise<any> {
    return this.restApiService.post(`payments/report-statistic`, {
      from: from,
      to: to,
      showCanceled: showCanceled,
      debitOnly: debitOnly,
      paymentType: paymentType,
      selectedSubPurposes: selectedSubPurposes,
    });
  }

  getMyExpenses(from: any, to: any, paymentType?: string, selectedSources?: string[]): Promise<any> {
    return this.restApiService.post(`payments/report`, {
      myExpenses: true,
      from: from,
      to: to,
      paymentType: paymentType,
      showCanceled: false,
      selectedSources: selectedSources,
    });
  }

  getMyExpensesTransactions(from: any, to: any, paymentType?: string, selectedSources?: string[]): Promise<any> {
    return this.restApiService.post(`payments/transactions-report`, {
      myExpenses: true,
      from: from,
      to: to,
      paymentType: paymentType,
      showCanceled: true,
      selectedSources: selectedSources,
    });
  }

  getClientBalance(clientId: string): Promise<BalanceData> {
    return this.restApiService.post(`payments/${clientId}/balance`);
  }

  getPublicClientBalance(ownerId: string, clientId: string): Promise<BalanceData> {
    return this.restApiService.get(`payments/${ownerId}/${clientId}/balance`);
  }

  getClientsBalance(clientIds: string[]): Promise<{ [clientId: string]: number }> {
    return this.restApiService.post(`payments/balance`, { clients: clientIds });
  }

  startBilling(from: any, to: any): Promise<any> {
    return this.restApiService.put(
      `payments/client/billing?from=${from.format('DD-MM-YYYY')}&to=${to.format(
        'DD-MM-YYYY'
      )}`
    );
  }

  getFuturePaymentsReport(
    from: any,
    to: any,
    studioIds: string[],
    includeRatePlansCalculation: boolean,
    clientsLimit?: number | string,
    purposes?: string[],
    subPurposes?: string[]
  ): Promise<any> {
    return this.restApiService.post('payments/report/future-income',
      {
        from: from.format('DD-MM-YYYY'),
        to: to.format('DD-MM-YYYY'),
        studioIds: studioIds,
        includeRatePlansCalculation: includeRatePlansCalculation,
        clientsLimit: clientsLimit,
        purposes: purposes,
        subPurposes: subPurposes,
      });
  }

  getRates(baseCurrency: string): Promise<any> {
    return this.restApiService.get(`payments/rates/${baseCurrency}`);
  }
}

export default PaymentApiCall;
